// Frontend client-error forwarder. Item 71 Phase E of
// docs/PRODUCT-ROADMAP.md. The browser captures runtime errors
// (window.error, unhandledrejection, explicit logError calls in
// hand-picked critical paths) and ships them in batches to
// `/api/v1/client-logs`, which re-emits them through the canonical
// applog logger so SIEM dashboards can pivot on
// `component=frontend` exactly the way they pivot on the API's own
// component catalogue.
//
// Buffer policy:
//   - Local queue capped at 50 events; the 51st pushes the oldest
//     out (we'd rather lose history than peg memory in a runaway
//     re-render loop).
//   - Flush on a 5 s ticker, or on `pagehide` (so the user
//     navigating away takes their tail with them via fetch
//     keepalive).
//   - The forwarder is best-effort: a 4xx / 5xx / network error
//     drops the batch silently. Console-warning the failure would
//     re-trigger our own `console.error` hook below and double-log.
//
// Debug mode: append `?debug=client-logs` to any URL — every
// outgoing batch is mirrored to console.debug. Useful when chasing
// "why isn't the SIEM seeing my error".

export type ClientLogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface ClientLogEvent {
  level: ClientLogLevel;
  msg: string;
  /** Page / component name. Pivots map onto applog's `subcomponent`. */
  subcomponent?: string;
  url?: string;
  user_agent?: string;
  occurred_at?: string;
  attrs?: Record<string, unknown>;
}

const ENDPOINT = '/api/v1/client-logs';
const MAX_QUEUE = 50;
const FLUSH_INTERVAL_MS = 5_000;
const MAX_BATCH = 50;

let queue: ClientLogEvent[] = [];
let installed = false;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let debugTrace = false;

/** Flag set when forwarding is unrecoverable (e.g. backend says 401);
 *  prevents the queue from growing without bound while users with
 *  stale sessions click around. */
let suspended = false;

function debug(...args: unknown[]) {
  if (!debugTrace) return;
  // eslint-disable-next-line no-console
  console.debug('[clientLogger]', ...args);
}

function pushEvent(ev: ClientLogEvent) {
  if (suspended) return;
  if (queue.length >= MAX_QUEUE) {
    // Drop the oldest — newest carries the most context for the
    // current user session.
    queue.shift();
  }
  queue.push(ev);
}

async function flush(useKeepalive = false) {
  if (suspended) return;
  if (queue.length === 0) return;

  // Take up to MAX_BATCH events and leave the rest for the next
  // tick. The backend caps the same number, so going higher would
  // 413.
  const batch = queue.splice(0, MAX_BATCH);
  const body = JSON.stringify({ events: batch });

  debug('flushing', batch.length, 'events');

  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
      // `keepalive` lets a small request survive page-unload — but
      // the spec caps it at 64 KiB total in flight, which is
      // exactly our backend ceiling.
      keepalive: useKeepalive,
    });

    if (res.status === 401 || res.status === 403) {
      // Caller isn't logged in (anymore). Stop trying — re-enable
      // is a page reload after fresh login, which re-runs install
      // and resets the flag.
      suspended = true;
      debug('forwarder suspended (auth)');
    }
  } catch {
    // Swallow — re-queueing on network failure would risk an
    // unbounded retry loop if the network stays out for a long
    // time.
  }
}

/** Public entry point. The signature accepts a free-form `err` so
 *  call sites can pass either an Error or a string without having
 *  to wrap things up themselves.
 *
 *  Reason for the name shape: matches `console.error` so existing
 *  call sites that already do `console.error('foo failed', err)`
 *  can be migrated to `clientLogger.error('foo failed', err, ctx)`
 *  without arg-order surprises.
 */
export const clientLogger = {
  debug(msg: string, ctx?: ClientLogContext): void {
    enqueue('debug', msg, undefined, ctx);
  },
  info(msg: string, ctx?: ClientLogContext): void {
    enqueue('info', msg, undefined, ctx);
  },
  warn(msg: string, err?: unknown, ctx?: ClientLogContext): void {
    enqueue('warn', msg, err, ctx);
  },
  error(msg: string, err?: unknown, ctx?: ClientLogContext): void {
    enqueue('error', msg, err, ctx);
  },
};

export interface ClientLogContext {
  /** Page / component name (becomes subcomponent in applog). */
  subcomponent?: string;
  /** Free-form structured context. Capped server-side at 8 KiB. */
  attrs?: Record<string, unknown>;
}

function enqueue(
  level: ClientLogLevel,
  msg: string,
  err: unknown,
  ctx: ClientLogContext | undefined,
) {
  const attrs: Record<string, unknown> = { ...ctx?.attrs };

  if (err instanceof Error) {
    attrs.error_name = err.name;
    attrs.error_message = err.message;
    if (err.stack) {
      // Cap the stack to the first ~3 KB so we don't blow the 8 KB
      // attrs budget on a single deep stack.
      attrs.error_stack = err.stack.length > 3072 ? err.stack.slice(0, 3072) + '…' : err.stack;
    }
  } else if (err !== undefined && err !== null) {
    try {
      attrs.error_value = typeof err === 'string' ? err : JSON.stringify(err);
    } catch {
      // err's type has no custom toString(); use the explicit default
      // stringification instead of relying on the implicit String(err) coercion.
      attrs.error_value = Object.prototype.toString.call(err);
    }
  }

  const ev: ClientLogEvent = {
    level,
    msg,
    subcomponent: ctx?.subcomponent,
    url: typeof globalThis === 'undefined' ? undefined : globalThis.location?.href,
    user_agent: typeof navigator === 'undefined' ? undefined : navigator.userAgent,
    occurred_at: new Date().toISOString(),
    attrs: Object.keys(attrs).length === 0 ? undefined : attrs,
  };
  pushEvent(ev);
}

/** Install global error handlers + flush ticker. Idempotent — calling
 *  twice is a no-op. Call once from main.tsx before render so we
 *  catch errors thrown during the initial render. */
export function installClientLogger(): void {
  if (installed) return;
  if (typeof globalThis === 'undefined') return;
  installed = true;

  try {
    debugTrace = new URLSearchParams(globalThis.location.search).get('debug') === 'client-logs';
  } catch {
    debugTrace = false;
  }

  globalThis.addEventListener('error', (event) => {
    const err = event.error instanceof Error ? event.error : new Error(event.message || 'window.error');
    enqueue('error', `window.error: ${event.message || err.message}`, err, {
      subcomponent: 'window',
      attrs: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  globalThis.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const err = reason instanceof Error ? reason : new Error(String(reason));
    enqueue('error', `unhandledrejection: ${err.message}`, err, {
      subcomponent: 'window',
    });
  });

  globalThis.addEventListener('pagehide', () => {
    void flush(true);
  });

  flushTimer = setInterval(() => {
    void flush(false);
  }, FLUSH_INTERVAL_MS);

  debug('installed');
}

/** Tear-down used by tests. Production never calls this. */
export function _resetClientLoggerForTests(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  queue = [];
  installed = false;
  suspended = false;
  debugTrace = false;
}
